import { useTranslation } from 'react-i18next';
import { DropButton } from 'grommet';
import { observer } from 'mobx-react-lite';
import flagQC from 'url:/public/images/flag_of_Quebec.png';
import { Box, Text } from '/src/components';
import { useUserStore } from '/src/context';

interface LanguageOption {
  label: string;
  value: string;
  flagSrc: string;
  flagSrcSet?: string;
}

interface LanguageSelectorProps {
  id?: string;
  position?: 'bottom' | 'top';
  dropAlign?: {
    top?: 'top' | 'bottom';
    bottom?: 'top' | 'bottom';
    right?: 'right' | 'left';
    left?: 'right' | 'left';
  };
  size?: 'small' | 'medium' | 'large';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = observer(({ id = 'language-selector' }) => {
  const { i18n } = useTranslation();
  const { isFuseAdmin } = useUserStore();

  if (isFuseAdmin) return null;

  const languageOptions: LanguageOption[] = [
    {
      label: 'English',
      value: 'en',
      flagSrc: 'https://flagcdn.com/w80/us.png',
      flagSrcSet: 'https://flagcdn.com/w160/us.png 2x',
    },
    { label: 'Français', value: 'fc', flagSrc: flagQC },
  ];

  const handleLanguageChange = (option: LanguageOption) => {
    i18n.changeLanguage(option.value);
  };

  const currentLanguage = languageOptions.find((option) => option.value === i18n.language) || languageOptions[0];

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '24px',
        zIndex: 1000,
        borderRadius: '20%',
        width: '60px',
        height: '60px',
        justifyContent: 'center',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      }}
    >
      <DropButton
        id={id}
        plain
        dropAlign={{ bottom: 'top', left: 'left' }}
        dropProps={{
          margin: { bottom: 'medium' },
          round: '6px',
          overflow: 'hidden',
        }}
        dropContent={
          <Box background="white">
            {languageOptions.map((option) => (
              <Box
                key={option.value}
                pad="small"
                hoverIndicator
                onClick={() => handleLanguageChange(option)}
                direction="row"
                align="center"
                gap="small"
                style={{ cursor: 'pointer' }}
              >
                <img
                  src={option.flagSrc}
                  srcSet={option.flagSrcSet}
                  width="20"
                  height="15"
                  alt={option.label}
                  style={{ borderRadius: '2px', objectFit: 'cover' }}
                />
                <Text size="small">{option.label}</Text>
              </Box>
            ))}
          </Box>
        }
      >
        <Box align="center" justify="center">
          <img
            src={currentLanguage.flagSrc}
            srcSet={currentLanguage.flagSrcSet}
            width="32"
            height="24"
            alt={currentLanguage.label}
            style={{ borderRadius: '3px', objectFit: 'cover' }}
          />
        </Box>
      </DropButton>
    </Box>
  );
});

export default LanguageSelector;
